import { useCallback, useEffect, useState } from 'react';
import useAuth from './useAuth';
import usePermissions from './usePermissions';
import useIncidentsService from '../services/incidentsService';
import { INCIDENTS_SECTION } from '../constants/permissions';

/** Cuánto se recuerda la respuesta de la API antes de volver a preguntar. */
export const ACCESS_TTL_MS = 2 * 60_000;

// Respuesta recordada por persona, y petición en vuelo, para que el menú y la guarda de
// la ruta (que montan por separado) no pregunten dos veces lo mismo.
const known = new Map();
const pending = new Map();

export const resetIncidentsAccessCache = () => {
  known.clear();
  pending.clear();
};

const remembered = (key) => {
  const entry = known.get(key);
  return entry && Date.now() - entry.at < ACCESS_TTL_MS ? entry : null;
};

/**
 * ¿Puede esta persona ver el centro de incidentes?
 *
 * La decisión la toma SIEMPRE la API: suma el permiso de la persona (su
 * `custom:permissions`) y el de su rol, en vivo. El frontend no la calcula por su cuenta,
 * porque nada garantiza que coincida: se limita a preguntar con una lectura mínima. Un
 * 403 es «no»; cualquier otro fallo es «no se sabe» (`error`) y se puede reintentar.
 *
 * Para no hacer que cada usuario de cada cliente genere un 403 al abrir la app, solo se
 * pregunta a quien tenga ALGUNA señal de poder tener el permiso: es personal de
 * plataforma, o su rol/persona lo trae marcado en la matriz de permisos. Es una pista
 * para ahorrar llamadas, nunca una concesión: sin respuesta afirmativa de la API, no hay
 * acceso.
 *
 * `status`: `loading` | `allowed` | `denied` | `error`.
 */
export default function useIncidentsAccess() {
  const { canAccessAllAccounts, userSub, user } = useAuth();
  const { permissions } = usePermissions();
  const { listIncidents } = useIncidentsService();
  const key = String(userSub || user?.user?.userId || 'anon');
  const mayHave = Boolean(canAccessAllAccounts) || permissions?.[INCIDENTS_SECTION] === true;

  const initial = () => {
    if (!mayHave) return 'denied';
    const hit = remembered(key);
    if (hit) return hit.allowed ? 'allowed' : 'denied';
    return 'loading';
  };
  const [status, setStatus] = useState(initial);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!mayHave) {
      setStatus('denied');
      return undefined;
    }
    const hit = remembered(key);
    if (hit) {
      setStatus(hit.allowed ? 'allowed' : 'denied');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    let request = pending.get(key);
    if (!request) {
      request = listIncidents({ limit: 1 })
        .then((result) => {
          if (result.success) {
            known.set(key, { allowed: true, at: Date.now() });
            return 'allowed';
          }
          if (result.status === 403) {
            known.set(key, { allowed: false, at: Date.now() });
            return 'denied';
          }
          return 'error';
        })
        .finally(() => pending.delete(key));
      pending.set(key, request);
    }
    request.then((next) => {
      if (!cancelled) setStatus(next);
    });
    return () => {
      cancelled = true;
    };
  }, [mayHave, key, listIncidents, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { status, allowed: status === 'allowed', retry };
}
